import { useMemo } from "react";
import "./App.css";
import { ErrorBoundary } from "@sentry/react";
import config from "./api/config";
import { Security, SecureRoute } from "./components/common/Security/Security";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback } from "@okta/okta-react";
import { Routes, Route } from "react-router-dom";
import routes from "./routes";
import DashboardPage from "components/DashboardPage/DashboardPage";
import CaseCompletePage from "components/CaseCompletePage/CaseCompletePage";
import SupervisorDashboard from "components/SupervisorDashboard/SupervisorDashboard";
import UserManagementSwitch from "components/UserManagement/UserManagementSwitch";
import FindACase from "components/FindACase/FindACase";
import ReportsDashboard from "components/Reports/Dashboard/ReportsDashboard";
import QueueVolumeDashboard from "components/Reports/QueueVolume/QueueVolumeDashboard";
import ProductivityDashboard from "components/Reports/Productivity/ProductivityDashboard";
import { NotFoundPage, TrackUsage, FeatureConfigurationProvider } from "@coherehealth/common";
import { error as logError, warn as logWarning } from "./logger";
import DefaultPageRouter from "util/DefaultPageRouter";
import WiserDownloadPage from "components/DashboardPage/WiserDownloadPage";
import { useOktaAuth } from "@okta/okta-react";

const oktaAuth = new OktaAuth({
  issuer: `${config.AUTH_API_URL}oauth2/default`,
  clientId: config.AUTH_CLIENT_ID,
  redirectUri: window.location.origin + "/implicit/callback",
  // Okta now recommends using "passive autorenew", eg renewing async when tokens are needed rather than trying to keep them up-to-date in a service.
  // https://github.com/okta/okta-auth-js/blob/64a548b9f0037887d1bf8908a76bf9f3848ee0d6/docs/autoRenew-notice.md
  tokenManager: {
    autoRenew: false,
    // Otherwise, on page load, old access tokens are deleted and the user is forced to log in again, even if session is not idle in okta
    autoRemove: false,
  },
  services: {
    autoRenew: false,
    autoRemove: false,
  },
  pkce: true,
});
function App() {
  return (
    <ErrorBoundary showDialog fallback={<>Unhandled exception!</>}>
      <Security
        restoreOriginalUri={async (_oktaAuth, originalUri) => {
          window.location.assign(toRelativeUrl(originalUri, window.location.origin));
        }}
        oktaAuth={oktaAuth}
      >
        <TrackUsage application="QM" baseUrl={config.QM_SERVICE_API_URL} />
        <AppWithSecurity />
      </Security>
    </ErrorBoundary>
  );
}
function AppWithSecurity() {
  const { authState, oktaAuth } = useOktaAuth();
  const getAccessToken = async () => {
    if (authState?.isAuthenticated) {
      return oktaAuth.getAccessToken();
    }
    return undefined;
  };
  const logger = useMemo(() => ({ error: logError, warn: logWarning }), []);

  return (
    <FeatureConfigurationProvider getAccessToken={getAccessToken} logger={logger}>
      <Routes>
        <Route
          path={routes.DASHBOARD}
          element={
            <SecureRoute>
              <DashboardPage />
            </SecureRoute>
          }
        />
        <Route
          path={routes.CASE_COMPLETE}
          element={
            <SecureRoute>
              <CaseCompletePage />
            </SecureRoute>
          }
        />
        <Route
          path={routes.SUPERVISOR_DASHBOARD}
          element={
            <SecureRoute>
              <SupervisorDashboard />
            </SecureRoute>
          }
        />
        <Route
          path={routes.USER_MANAGEMENT}
          element={
            <SecureRoute>
              <UserManagementSwitch />
            </SecureRoute>
          }
        />
        <Route
          path={routes.FIND_A_CASE}
          element={
            <SecureRoute>
              <FindACase />
            </SecureRoute>
          }
        />
        <Route
          path={routes.REPORTS.DASHBOARD}
          element={
            <SecureRoute>
              <ReportsDashboard />
            </SecureRoute>
          }
        />
        <Route
          path={routes.REPORTS.QUEUE_VOLUME}
          element={
            <SecureRoute>
              <QueueVolumeDashboard />
            </SecureRoute>
          }
        />
        <Route
          path={routes.REPORTS.PRODUCTIVITY}
          element={
            <SecureRoute>
              <ProductivityDashboard />
            </SecureRoute>
          }
        />
        <Route path="/implicit/callback" element={<LoginCallback />} />
        <Route
          path="/"
          element={
            <SecureRoute>
              <DefaultPageRouter />
            </SecureRoute>
          }
        />
        <Route path={routes.CYPRESS_LANDING} element={<NotFoundPage />} />
        <Route
          path="*"
          element={
            <SecureRoute>
              <NotFoundPage />
            </SecureRoute>
          }
        />
        <Route
          path={routes.WISER_DOWNLOAD}
          element={
            <SecureRoute>
              <WiserDownloadPage />
            </SecureRoute>
          }
        />
      </Routes>
    </FeatureConfigurationProvider>
  );
}
export default App;
